/**
 * Which effect plays, and when.
 *
 * `renderFx` has produced five effects since the art package existed, the asset
 * manifest counted them, and the client drew none of them — 절대 규칙 11 ("만들었으면
 * 화면에 띄운다") in its purest form. This module is the missing half.
 *
 * The rules are kept here, away from React, so they can be tested as data: an
 * effect is a reaction to something the engine already announced, never a state
 * the client invents.
 */

import { FX_KINDS, type FxKind } from '@lastroll/art';
import type { SessionEvent } from '@lastroll/engine';

/**
 * Where an effect sits on the stage and how long it runs.
 *
 * Typed as a total Record on purpose: add a sixth kind to `FX_KINDS` and this
 * file stops compiling until the client decides where it goes. That is the
 * structural version of "만들었으면 화면에 띄운다" — a generated-but-invisible
 * asset becomes a build error rather than something nobody notices for weeks.
 */
export const FX_PLAY: Readonly<
  Record<FxKind, { readonly anchor: 'centre' | 'tray' | 'floor'; readonly ms: number }>
> = {
  critBurst: { anchor: 'tray', ms: 700 },
  fateDebtPulse: { anchor: 'tray', ms: 900 },
  discoveryRing: { anchor: 'centre', ms: 900 },
  shimmer: { anchor: 'centre', ms: 1000 },
  dust: { anchor: 'floor', ms: 800 },
};

export interface FxCue {
  readonly kind: FxKind;
  /** Bumped per cue so replaying the same effect restarts the animation. */
  readonly id: number;
}

/**
 * How loudly each effect speaks. Lower wins.
 *
 * A turn can drain a move, a manipulation and a roll at once, so the batch needs
 * a ranking rather than a first-match. The first version took whichever event
 * came first and a discovery lost to the step that walked into it.
 */
const RANK: Readonly<Record<FxKind, number>> = {
  critBurst: 0,
  shimmer: 1,
  fateDebtPulse: 2,
  discoveryRing: 3,
  dust: 4,
};

/**
 * One effect per drained batch, most significant first.
 *
 * Firing every effect a turn produced would read as noise rather than emphasis.
 */
export function fxFor(events: readonly SessionEvent[]): FxKind | null {
  const candidates: FxKind[] = [];

  for (const ev of events) {
    switch (ev.kind) {
      case 'rolled': {
        const record = ev.data?.['record'] as { band?: string } | undefined;
        // A critical is the one roll the player should feel before reading it.
        if (record?.band === 'critical') candidates.push('critBurst');
        break;
      }
      // Titles, hidden classes and easter eggs are the game noticing the player.
      case 'titleEarned':
      case 'hiddenClass':
      case 'easterEgg':
      case 'streak':
        candidates.push('shimmer');
        break;
      // Bending a roll costs Fate Debt; the pulse is the debt being taken on.
      case 'manipulated':
      case 'fateEvent':
      case 'promiseBroken':
        candidates.push('fateDebtPulse');
        break;
      case 'encounterOpened':
        candidates.push('discoveryRing');
        break;
      case 'moved':
        candidates.push('dust');
        break;
      default:
        break;
    }
  }

  if (candidates.length === 0) return null;
  return candidates.reduce((best, k) => (RANK[k] < RANK[best] ? k : best));
}

/** Every effect the art package generates must appear in at least one rule above. */
export const FX_TRIGGERS: Readonly<Record<FxKind, readonly SessionEvent['kind'][]>> = {
  critBurst: ['rolled'],
  shimmer: ['titleEarned', 'hiddenClass', 'easterEgg', 'streak'],
  fateDebtPulse: ['manipulated', 'fateEvent', 'promiseBroken'],
  discoveryRing: ['encounterOpened'],
  dust: ['moved'],
};

export { FX_KINDS, type FxKind };
